<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useHead, useI18n, useRuntimeConfig } from '#imports'
import { getMaintenanceLabels } from './labels'
import MinigolfGame from './MinigolfGame.vue'

defineOptions({ inheritAttrs: false })

// Localized maintenance copy lives as raw HTML snippets in `content/`, one file
// per language, reused from the previous static maintenance page.
const contentModules = import.meta.glob('./content/*.html', {
    query: '?raw',
    import: 'default',
    eager: true
}) as Record<string, string>

const contentByLang: Record<string, string> = {}
for (const path in contentModules) {
    const lang = path.split('/').pop()?.replace('.html', '')
    if (lang) contentByLang[lang] = contentModules[path]!
}

const { locale } = useI18n()
const lang = computed(() => (locale.value || 'en').slice(0, 2).toLowerCase())
const content = computed(() => contentByLang[lang.value] ?? contentByLang.en ?? '')
const labels = computed(() => getMaintenanceLabels(lang.value))

useHead({
    title: () => `${labels.value.documentTitle} - All4Golf`
})

// Strokes of the hole the visitor finished last, shown below the game.
const lastResult = ref<number | null>(null)

// Auto-retry: reloading blindly every 30s would throw away a running game, so
// instead probe the store-api from the browser (real visitor IP, so Shopware's
// maintenance whitelist decides) and only reload once the shop answers again.
// The same probe runs when the tab is refocused.
//
// Deliberately a plain $fetch and not the Shopware api client: the client carries
// the module's global `onResponseError` hook, which turns exactly this 503 into
// `showError({ statusCode: 503, statusMessage: 'MAINTENANCE_MODE' })` — the very
// error this page renders. Probing through it would re-trigger that error every
// 30s while the shop is still down, and its `onContextChanged` hook would rewrite
// the sw-context-token cookie. A health check must stay free of both.
const RETRY_INTERVAL_MS = 30000
const config = useRuntimeConfig()
let timer: ReturnType<typeof setInterval> | undefined
let probing = false

async function probeAndReload() {
    const endpoint = config.public.shopware?.endpoint
    const accessToken = config.public.shopware?.accessToken
    if (!endpoint || !accessToken || probing) return

    probing = true
    try {
        await $fetch(`${endpoint.replace(/\/$/, '')}/context`, {
            headers: { 'sw-access-key': accessToken }
        })
        window.location.reload()
    } catch {
        // Still in maintenance — keep the page, and the running game, alive.
    } finally {
        probing = false
    }
}

function onVisibilityChange() {
    if (document.visibilityState === 'visible') probeAndReload()
}

onMounted(() => {
    timer = setInterval(probeAndReload, RETRY_INTERVAL_MS)
    document.addEventListener('visibilitychange', onVisibilityChange)
})

onBeforeUnmount(() => {
    if (timer) clearInterval(timer)
    document.removeEventListener('visibilitychange', onVisibilityChange)
})
</script>

<template>
    <main class="min-h-screen bg-base-100 px-4 py-10 md:py-16">
        <div class="mx-auto max-w-[760px] text-center">
            <!-- eslint-disable-next-line vue/no-v-html -->
            <div class="leading-relaxed text-base-content [&_h1]:mb-4 [&_h1]:font-fjalla-one [&_h1]:text-3xl [&_h1]:uppercase [&_h1]:leading-tight md:[&_h1]:text-4xl [&_p]:mb-4 [&_strong]:font-bold" v-html="content" />

            <p class="mb-4 mt-8 text-sm text-base-content/70">
                {{ labels.gameIntro }}
            </p>

            <ClientOnly>
                <MinigolfGame
                    :labels="labels.game"
                    @holed="lastResult = $event"
                />
                <template #fallback>
                    <div class="mx-auto aspect-[800/520] w-full max-w-[760px] animate-pulse rounded-2xl bg-base-300" />
                </template>
            </ClientOnly>

            <p
                v-if="lastResult !== null"
                class="mt-4 text-sm text-base-content/60"
            >
                {{ labels.lastResult(lastResult) }}
            </p>
        </div>
    </main>
</template>
